/**
 * @file 开始使用页快速安装hooks
 * @author FengGuang(fengguang01@baidu.com)
 */
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';

import useQuickStartHooks from '../../components/quick-start/quickStartHooks';
import {
    fetchGetQuickInstall,
    fetchGetQuickInstallDetail
} from '../../../../api/pages/installApi';
import { IQuickStartItem } from '../../components/quick-start/type';

const getDocUrl = (search: string) => {
    return new URLSearchParams(search.replace(/^\?/, '')).get('docurl') || '';
};

const useQuickHooks = () => {
    const match = useRouteMatch();
    const matchRef = useLatest(match);
    const location = useLocation();
    const locationRef = useLatest(location);
    const history = useHistory();
    const historyRef = useLatest(history);
    const quickStartHooks = useQuickStartHooks('quickInstallHooksChecked');

    const [suffixText, setSuffixText] = useState<string>('');

    // 获取快速安装选择器数据
    const qiShowItem = quickStartHooks.showItem;
    const qsSetChecked = quickStartHooks.setChecked;
    const qsSetQuickInstallList = quickStartHooks.setQuickInstallList;
    const qsSetQuickInstallSelector = quickStartHooks.setQuickInstallSelector;

    const resJson = localStorage.getItem('quickInstallHooksChecked');
    const resArray: string[] = useMemo(() =>
        resJson ? JSON.parse(resJson) : []
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);

    useEffect(() => {
        (async () => {
            const res = await fetchGetQuickInstall()
                .catch(() => {
                    // ignore
                });
            if (res) {
                const data = res.body.result?.pageData || {};

                const qi: any = data.quickInstall || {};
                const qiGroup: any[] = Array.isArray(qi.group) ? qi.group : [];
                const qiList: any[] = Array.isArray(qi.list) ? qi.list : [];

                // 选项
                const resGroup = qiGroup.map(groupItem => {
                    const list: any[] = Array.isArray(groupItem.list) ? groupItem.list : [];
                    return {
                        groupName: groupItem.groupName,
                        groupKey: groupItem.groupKey || groupItem.groupName,
                        disabledTooltip: groupItem.disabledTooltip,
                        tips: groupItem.tips,
                        list: list.map(item => ({
                            id: `${groupItem.groupName}${item.optionName}${item.optionKey}`,
                            groupName: groupItem.groupName,
                            groupKey: groupItem.groupKey || groupItem.groupName,
                            optionName: item.optionName,
                            optionKey: item.optionKey
                        }))
                    };
                });
                // 可选的结果列表
                const resQiList: IQuickStartItem[] = qiList.map(item => {
                    const theContext: any[] = Array.isArray(item.context) ? item.context : [];
                    return {
                        index: item.index,
                        indexSet: typeof (item.index) === 'string' ? new Set<string>(item.index.split(',')) : new Set<string>(),
                        context: theContext.map((citem) => ({
                            title: citem.title,
                            text: citem.text
                        })),
                        linkEn: item.linkEn,
                        linkZh: item.linkZh
                    };
                });

                // 一定要先设置选中项 checked，再设置可选列表 qiList。
                // 因为选中项会从localstorage读取默认值，先设置可选项，再判断一次默认值，可能导致监听选中结果的effect多执行一次

                // 快速安装默认选中的项目
                const qiDefaultChecked = qi.defaultSelectedKey ? qi.defaultSelectedKey.split(',') : [];
                const docurl = getDocUrl(locationRef.current.search.slice(1));

                if (docurl && docurl !== 'undefined') {
                    // 如果url已经指定了一篇文档，就将快速安装设置成对应的选中状态
                    const isInclude = (resArray: string[], str: string): boolean => {
                        let booleanStatus = true;
                        resArray.forEach(ele => {
                            if (!str.includes(ele)) {
                                booleanStatus = false;
                            }
                        });
                        return booleanStatus;
                    };

                    const item = resQiList.find(i => {
                        if (resArray.length > 0) {
                            return (
                                (i.linkZh && (i.linkZh.indexOf(docurl) > -1 || i.linkEn.indexOf(docurl) > -1))
                                && isInclude(resArray, i.index)
                            );
                        }
                        return i.linkZh && (i.linkZh.indexOf(docurl) > -1 || i.linkEn.indexOf(docurl) > -1);
                    });

                    if (item) {
                        qsSetChecked(Array.from(item.indexSet));
                    }
                }
                else {
                    const existKey = new Set();
                    resGroup.forEach((group) => {
                        group.list.forEach((item) => {
                            existKey.add(item.optionKey);
                        });
                    });
                    qsSetChecked((old) => {
                        const def = old.filter(k => existKey.has(k));
                        return (
                            def.length > 0 ? def : qiDefaultChecked
                        );
                    });
                }
                qsSetQuickInstallList(resQiList);
                qsSetQuickInstallSelector(resGroup);

                // 设置后缀文案
                const st = qi.suffixText || '';
                setSuffixText(st);
            }
        })();
    }, [qsSetQuickInstallList, qsSetChecked, qsSetQuickInstallSelector, matchRef, locationRef, resArray]);

    // 获取快速安装详情数据
    const [quickInstallDetailCache, setQuickInstallDetailCache] = useState<{ [key: string]: string | undefined }>({});
    useEffect(() => {
        (async () => {
            if (match.path !== '/install/quick') {
                return;
            }

            const docUrl = getDocUrl(location.search.slice(1));

            if (!docUrl || docUrl === 'undefined') {
                setQuickInstallDetailCache((old) => {
                    return {
                        ...old,
                        [docUrl]: ''
                    };
                });
                return;
            }

            const res = await fetchGetQuickInstallDetail(docUrl)
                .catch(() => {
                    // ignore
                });
            if (res) {
                if (res.text) {
                    const tmp = document.createElement('html');
                    tmp['innerHTML'] = res.text;
                    const theDom = tmp.querySelector('#document_content .document');

                    if (theDom) {
                        if (theDom.getAttribute('th:utext')
                            || theDom.getAttribute('th:text')) {
                            return;
                        }

                        const anchorlist = theDom.querySelectorAll<HTMLElement>('.headerlink');
                        anchorlist.forEach((item) => {
                            const html = item.innerText;
                            item.innerText = html.replace(/¶/g, '');
                        });

                        setQuickInstallDetailCache((old) => {
                            return {
                                ...old,
                                [docUrl]: theDom.outerHTML
                            };
                        });
                    }
                }
            }
        })();
    }, [match.path, location.search, location.pathname]);

    const quickInstallDetail = useMemo(() => {
        const docurl = getDocUrl(location.search.slice(1));
        return quickInstallDetailCache[docurl] || '';
    }, [quickInstallDetailCache, location.search]);


    // 语言选择框
    const language = useMemo(() => {
        const docurl = getDocUrl(location.search.slice(1));
        if (qiShowItem?.linkZh && qiShowItem.linkZh.indexOf(docurl) > -1) {
            return 'zh';
        }
        else if (qiShowItem?.linkEn && qiShowItem.linkEn.indexOf(docurl) > -1) {
            return 'en';
        }
        return 'zh';
    }, [location.search, qiShowItem]);

    const setLanguage = useCallback((value: string) => {
        if (qiShowItem) {
            const oldLink = getDocUrl(locationRef.current.search.slice(1));
            const oldLinkZhIndex = qiShowItem.linkZh?.indexOf(oldLink) ?? -1;
            const linkEn = qiShowItem.linkEn?.[oldLinkZhIndex];
            const oldLinkEnIndex = qiShowItem.linkEn?.indexOf(oldLink) ?? -1;
            const linkZh = qiShowItem.linkZh?.[oldLinkEnIndex];

            if (value === 'zh' && linkZh) {
                historyRef.current.push(`?docurl=${linkZh}`);
            }
            else if (value === 'en' && linkEn) {
                historyRef.current.push(`?docurl=${linkEn}`);
            }
        }
    }, [historyRef, locationRef, qiShowItem]);

    useEffect(() => {
        const docurl = getDocUrl(location.search.slice(1));
        if (language === 'zh' && qiShowItem?.linkZh && qiShowItem.linkZh.indexOf(docurl) === -1) {
            historyRef.current.push(`?docurl=${qiShowItem.linkZh[0]}`);
        }
        else if (language === 'en' && qiShowItem?.linkEn && qiShowItem.linkEn.indexOf(docurl) === -1) {
            historyRef.current.push(`?docurl=${qiShowItem.linkEn[0]}`);
        }
    }, [qiShowItem, historyRef, location.search, language]);

    // 根据 hash 跳转到锚点位置
    const scrollToAnchor = useCallback(() => {
        const anchorName = location.hash.slice(1);
        if (!anchorName) {
            return;
        }
        const anchorDom = document.getElementById(anchorName)
            || document.querySelector(`a[name="${anchorName}"]`);
        if (anchorDom) {
            anchorDom.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [location.hash]);
    useEffect(scrollToAnchor, [location.hash, quickInstallDetail, scrollToAnchor]);

    return {
        quickStartHooks,
        quickInstallDetail,
        suffixText,
        language,

        setLanguage
    };
};

export default useQuickHooks;
